import { SelectedTheme } from "@revolt/state/stores/Theme";

/**
 * Generate Stoat for Web variables
 * @param theme Theme
 * @returns CSS Variables
 */
export function createStoatWebVariables(theme: SelectedTheme) {
  return {
    // helper variables
    "--unset-fg": "red",
    "--unset-bg": "linear-gradient(to right, red, blue)",

    // message size
    "--message-size": `${theme.messageSize}px`,
    "--message-group-spacing": `${theme.messageGroupSpacing}px`,

    // emoji size
    "--emoji-size": "1.4em",
    "--emoji-size-medium": "48px",
    "--emoji-size-large": "96px",

    // effects
    "--effects-blur-md": theme.blur ? "blur(20px)" : "unset",
    "--effects-invert-black": theme.darkMode ? "invert(100%)" : "invert(0%)",
    "--effects-invert-light": theme.darkMode ? "invert(0%)" : "invert(1000%)",

    // transitions
    "--transitions-fast": ".1s ease-in-out",
    "--transitions-medium": ".2s ease",

    // brand
    "--brand-presence-online": "#3ABF7E",
    "--brand-presence-idle": "#F39F00",
    "--brand-presence-busy": "#F84848",
    "--brand-presence-focus": "#4799F0",
    "--brand-presence-invisible": "#A5A5A5",

    // font
    "--fonts-primary": `"${theme.interfaceFont}", "Inter", sans-serif`,
    "--fonts-monospace": `"${theme.monospaceFont}", "Jetbrains Mono", sans-serif`,

    // load constants
    ...reduceWithPrefix(themeConstants.borderRadius, "--borderRadius-"),
    // pill radii — replaces hardcoded 48px/64px/160px in Button component
    "--borderRadius-pill-sm": "2px",
    "--borderRadius-pill-md": "2px",
    "--borderRadius-pill-lg": "3px",
    ...reduceWithPrefix(themeConstants.gap, "--gap-"),
    ...reduceWithPrefix(themeConstants.layout, "--layout-"),

    // Tiaborp: override M3 surface colours for a distinct cyberpunk palette
    ...(theme.darkMode ? tiaborpDarkSurfaces : {}),
  };
}

/**
 * Tiaborp dark mode surface overrides — replaces M3's washed-out darks
 * with a deep teal-tinted panel hierarchy
 */
const tiaborpDarkSurfaces: Record<string, string> = {
  // base backgrounds — near-black with blue-black tint
  "--md-sys-color-surface-dim":              "#050709",
  "--md-sys-color-surface":                  "#080b0e",
  "--md-sys-color-surface-bright":           "#0f1720",

  // container hierarchy — progressively lighter teal-tinted darks
  "--md-sys-color-surface-container-lowest": "#060a0d",
  "--md-sys-color-surface-container-low":    "#0c1520",
  "--md-sys-color-surface-container":        "#101c28",
  "--md-sys-color-surface-container-high":   "#142230",
  "--md-sys-color-surface-container-highest":"#1a2a38",

  // text — cool blue-white instead of warm white
  "--md-sys-color-on-surface":               "#cce0e8",
  "--md-sys-color-on-surface-variant":       "#7aa0b0",

  // dividers — vivid teal panel lines
  "--md-sys-color-outline":                  "#0d9ea4",
  "--md-sys-color-outline-variant":          "#0a5a60",

  // primary teal — brighter/more saturated than M3's muted version
  "--md-sys-color-primary":                  "#00d4db",
  "--md-sys-color-on-primary":               "#001a1b",
  "--md-sys-color-primary-container":        "#003638",
  "--md-sys-color-on-primary-container":     "#a0f0f4",

  // error/red — vivid crimson
  "--md-sys-color-error":                    "#c8213a",
  "--md-sys-color-on-error":                 "#ffffff",
  "--md-sys-color-error-container":          "#4a0d18",
  "--md-sys-color-on-error-container":       "#ffb3bc",
};

/**
 * Add prefix to all keys in an object
 * @param object Object
 * @param prefix Prefix
 * @returns New object
 */
function reduceWithPrefix(object: Record<string, string>, prefix: string) {
  return Object.entries(object).reduce(
    (d, [k, v]) => ({ ...d, [`${prefix}${k}`]: v }),
    {},
  );
}

const themeConstants = {
  borderRadius: {
    // Tiaborp: hard edges — cyberpunk aesthetic
    none: "0px",
    xs: "0px",
    sm: "1px",
    md: "2px",
    lg: "2px",
    li: "2px",
    xl: "3px",
    xli: "3px",
    xxl: "4px",
    full: "calc(infinity * 1px)",
    circle: "100%",
  },
  /**
   * @deprecated decide this at a component level
   */
  gap: {
    none: "0",
    xxs: "1px",
    xs: "2px",
    s: "6px",
    sm: "4px",
    md: "8px",
    l: "12px",
    lg: "15px",
    x: "28px",
    xl: "32px",
    xxl: "64px",
  },
  layout: {
    "width-channel-sidebar": "248px",
    "width-user-context-menu-truncate": "300px",
  },
};
